// =====================================================================
// Chamados — as ABAS da Consulta (ESPELHO do backend).
//
// ⚠️⚠️ ISTO É ESPELHO de `server/chamados/chamados-abas.js`, não uma segunda
//    fonte. A divisão de status por aba existe dos dois lados porque:
//
//    1. o servidor estreita na query (índice, payload menor);
//    2. a TELA estreita de novo no cliente — e essa segunda passada é o que mata
//       a JANELA DE DEPLOY. O `npm run build` escreve o `build/` antes do
//       `pm2 reload`, então por alguns segundos a tela é a nova e o Node é o
//       antigo (ver `CLAUDE.md` › Deploy). A tela nova pedindo `?aba=triagem` a
//       um backend velho recebe TUDO SEM FILTRO de status (o `if/else` de lá não
//       casa e cai em "todos"). Sem o estreitamento daqui, *Em aberto* mostraria
//       validado e cancelado misturados, **sem erro nenhum**.
//
// ⚠️ Divergir do backend não dá erro em lugar nenhum — dá aba que mostra a linha
//    errada. Há teste de espelho (`server/tests/chamados/chamados-abas.test.js`)
//    e ele confere por TEXTO: mantenha **uma aba por linha**, nos dois arquivos.
//
// ⚠️ Este arquivo é irmão do `chamados-fila-utils.js` (o outro espelho da pasta)
//    e NÃO do `chamados-filtros.js`, que é regra só de tela.
// =====================================================================

// ⚠️ O tipo e o predicado do chip moram no `chamados-chip-utils.js` (o espelho de
//    tela do módulo do chip, travado contra o backend). Importar de lá mantém UMA
//    fonte da string do tipo no front, em vez de repeti-la aqui.
import { ehChip } from './chamados-chip-utils';

/**
 * As abas da esteira, na ordem em que aparecem na tela.
 *
 * ⚠️ Os rótulos vivem aqui e não no JSX: são eles que o teste de espelho lê, e
 *    é aqui que se muda o texto de um botão.
 */
export const ABAS_ESTEIRA = [
    { id: 'triagem', rotulo: 'Em aberto', status: ['Aberto'] },
    { id: 'atribuidos', rotulo: 'Atribuído', status: ['Atribuido'] },
    { id: 'atendimento', rotulo: 'Em atendimento', status: ['Em atendimento', 'Atendimento Pausado', 'Aguardando loja', 'Concluido'] },
    // ⚠️ Espelha o server: 'Finalizado' (espera de terceiro — Sonda/peças) tem
    //    aba própria desde 21/09 (DEM-00009). Uma aba por linha, para o teste de
    //    espelho conferir por texto.
    { id: 'aguardando-terceiro', rotulo: 'Aguardando terceiro', status: ['Finalizado'] },
    { id: 'encerrados', rotulo: 'Encerrado', status: ['Encerrado', 'Validado'] },
    { id: 'cancelados', rotulo: 'Cancelados', status: ['Cancelado'] },
];

/**
 * A aba POR TIPO da solicitação de chip (DEM-00027) — espelho do backend.
 *
 * ⚠️ `status: null` porque ela NÃO filtra por status: o chip reusa os status da
 *    esteira, então quem recorta é o TIPO. Fica FORA de `ABAS_ESTEIRA` (e do
 *    espelho por status, que confere só as abas com `status: [...]`).
 */
export const ABA_CHIP = { id: 'chip', rotulo: 'Chip celular', status: null, tipo: 'chip' };

/** A aba que não filtra nada. */
export const ABA_TODOS = { id: 'todos', rotulo: 'Todos', status: null };

/** Todas as abas da tela, na ordem — é esta lista que o JSX percorre.
 *  ⚠️ Chip entra logo ANTES de 'Todos' (espelho do backend). */
export const ABAS = [...ABAS_ESTEIRA, ABA_CHIP, ABA_TODOS];

/**
 * Quais STATUS esta aba mostra. `null` = não filtre nada.
 *
 * ⚠️ Aba desconhecida devolve `null` (= todos), nunca lista vazia: lista vazia
 *    se lê como *não há registro*.
 */
export function statusDaAba(aba) {
    const a = ABAS_ESTEIRA.find((x) => x.id === aba);
    return a ? a.status : null;
}

/**
 * Em qual aba da esteira este chamado aparece — é assim que o CONTADOR agrupa a
 * contagem por status que vem do servidor.
 */
export function abaDoStatus(status) {
    const a = ABAS_ESTEIRA.find((x) => x.status.includes(status));
    return a ? a.id : null;
}

/**
 * Filtra a lista pela aba, no cliente.
 *
 * ⚠️ Ver o item 2 do cabeçalho: esta função é a rede contra o backend velho.
 *    Ela é redundante com o filtro do servidor **em regime normal**, e é isso
 *    que se quer — nunca "simplificar" removendo-a.
 */
export function filtrarPelaAba(chamados, aba) {
    // ⚠️⚠️ A ABA DE CHIP mostra SÓ o chip; as DEMAIS o excluem (DEM-00027). É a
    //    rede contra o backend velho da janela de deploy: um Node antigo não
    //    conhece a aba 'chip' e devolve tudo sem filtro, e como o chip reusa os
    //    status da esteira, ele apareceria nas abas de status sem este recorte.
    if (aba === ABA_CHIP.id) return chamados.filter((c) => ehChip(c?.TIPO_SOLICITACAO));
    const semChip = chamados.filter((c) => !ehChip(c?.TIPO_SOLICITACAO));
    const permitidos = statusDaAba(aba);
    if (!permitidos) return semChip;               // 'todos' (ou aba desconhecida)
    return semChip.filter((c) => permitidos.includes(c.STATUS));
}

/**
 * Distribui a contagem por STATUS que veio do `GET /contagem` nas abas.
 *
 * @param {object} porStatus  `{ Aberto: 3, Encerrado: 5, ... }`
 * @returns {object} `{ triagem: 3, encerrados: 5, ..., todos: 8 }`
 *
 * ⚠️⚠️ STATUS SEM ABA É SOMADO EM `todos` MAS NÃO SE PERDE do total — se algum
 *    dia entrar um status novo no `ENUM` sem passar por aqui, o número de
 *    *Todos* fica maior que a soma das abas. É feio e visível, que é o
 *    comportamento desejado: melhor a soma não fechar do que o chamado
 *    desaparecer calado (o teste de exaustividade do backend impede o caso, mas
 *    a tela não deve depender disso para não mentir).
 */
export function contagemPorAba(porStatus) {
    const fora = {};
    let total = 0;
    for (const [chave, n] of Object.entries(porStatus || {})) {
        const quantos = Number(n) || 0;
        // ⚠️ A chave `chip` (DEM-00027) é a aba POR TIPO, fora da esteira: vai
        //    direto no botão dela e NÃO entra no total de 'Todos' (o backend
        //    exclui o chip da esteira e do 'Todos' também).
        if (chave === 'chip') { fora.chip = quantos; continue; }
        total += quantos;
        const aba = abaDoStatus(chave);
        if (aba) fora[aba] = (fora[aba] || 0) + quantos;
    }
    fora.todos = total;
    return fora;
}
